/**
 * Campagnes d'UNE organisation — portail admin (plan du 2026-08-26, phase 3).
 *
 * Point d'entrée de la navigation hiérarchique : le dashboard des organisations
 * mène ici, et d'ici on descend vers les candidats ou les évaluateurs d'une
 * campagne. Aucun parcours n'est lu par cette page : elle ne liste que des
 * campagnes, les parcours ne se chargent qu'une fois entré dans l'une d'elles.
 *
 * Sert deux cas : les campagnes d'une organisation, et celles qui n'en ont pas
 * (`/sans-organisation/campagnes`, données d'avant le multi-tenant).
 */
import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  DataTable,
  PageContainer,
  PageHeader,
  useFiltresColonnesUrl,
} from "@repo/ui";
import { useMutation, useQuery } from "convex/react";
import { Plus, Route } from "lucide-react";
import { useMemo, useState } from "react";
import { ConfirmerSuppression } from "../../components/ConfirmerSuppression";
import { Link, useParams } from "react-router";
import { api } from "@convex/_generated/api";
import { arianeOrganisation } from "../../lib/ariane";
import type { Id } from "@convex/_generated/dataModel";
import {
  colonnesCampagnesOrg,
  ETAT_ARCHIVE,
  FACETTES_CAMPAGNES_ORG,
  RECHERCHE_CAMPAGNES_ORG,
  type ActionsCampagneOrg,
  type CampagneOrgLigne,
} from "./colonnesCampagnesOrg";

/** Références stables : recréées à chaque rendu, elles feraient boucler les mémos. */
const CHAMPS_FILTRES = {
  q: { colonne: RECHERCHE_CAMPAGNES_ORG.colonne },
  statut: { colonne: "statut", multi: true },
  epreuves: { colonne: "applications", multi: true },
  groupe: { colonne: "groupe", multi: true },
  template: { colonne: "templates", multi: true },
  archive: { colonne: "archive", multi: true },
} as const;

/**
 * Sans filtre d'archivage dans l'URL, la liste ne montre que les campagnes
 * actives. Les archivées se retrouvent en cochant « Archivées » dans la facette
 * « Archivage ». Injecté ici plutôt que dans l'URL, pour qu'une vue par défaut
 * garde une adresse propre.
 */
const FILTRE_ARCHIVE_DEFAUT = {
  id: "archive",
  value: [ETAT_ARCHIVE.ACTIVES],
};
const BARRE_FILTRES = {
  recherche: RECHERCHE_CAMPAGNES_ORG,
  facettes: FACETTES_CAMPAGNES_ORG,
};

export function CampagnesOrganisation() {
  // Route sans `:orgId` = `/sans-organisation/campagnes`.
  const { orgId } = useParams<{ orgId?: string }>();
  const organisationId = orgId as Id<"organisations"> | undefined;

  const [filtres, setFiltres] = useFiltresColonnesUrl(CHAMPS_FILTRES);

  // Une seule souscription, sans curseur : le `DataTable` trie, filtre et
  // facette sur la totalité des campagnes de l'organisation, sinon les
  // compteurs de facette ne parleraient que de la première page.
  const vue = useQuery(api.superAdmin.listerCampagnesOrgVue, {
    organisationId,
    aucuneOrg: organisationId ? undefined : true,
  });

  const organisation = useQuery(
    api.superAdmin.obtenirOrganisation,
    organisationId ? { organisationId } : "skip",
  );

  const archiver = useMutation(api.superAdmin.archiverCampagne);
  const [aSupprimer, setASupprimer] = useState<CampagneOrgLigne | null>(null);
  const desarchiver = useMutation(api.superAdmin.desarchiverCampagne);

  const actions = useMemo<ActionsCampagneOrg>(
    () => ({
      onArchiver: (ligne: CampagneOrgLigne) => {
        if (
          confirm(
            `Archiver « ${ligne.nom} » ?\n\nLes candidats et les évaluateurs n'auront plus accès à leurs liens. Rien n'est supprimé : la campagne peut être désarchivée.`,
          )
        ) {
          void archiver({ campagneId: ligne._id });
        }
      },
      onDesarchiver: (ligne: CampagneOrgLigne) => {
        void desarchiver({ campagneId: ligne._id });
      },
      onSupprimer: (ligne: CampagneOrgLigne) => setASupprimer(ligne),
    }),
    [archiver, desarchiver],
  );

  const colonnes = useMemo(() => colonnesCampagnesOrg(actions), [actions]);

  // Le filtre par défaut ne s'applique que si l'utilisateur n'a pas lui-même
  // touché à la facette « Archivage » (auquel cas son choix figure dans l'URL).
  const filtresEffectifs = useMemo(
    () =>
      filtres.some((f) => f.id === "archive")
        ? filtres
        : [...filtres, FILTRE_ARCHIVE_DEFAUT],
    [filtres],
  );

  // Le tableau masque les archivées par défaut : compter toutes les lignes ici
  // afficherait un total que l'utilisateur ne retrouve pas dans la liste.
  const nbActives = (vue?.lignes ?? []).filter((l) => !l.archivedAt).length;

  const nomOrganisation = organisationId
    ? (organisation?.nom ?? "Organisation")
    : "Sans organisation";

  const horsCampagneHref = organisationId
    ? `/organisations/${organisationId}/hors-campagne`
    : "/sans-organisation/hors-campagne";

  return (
    <PageContainer>
      <Breadcrumb items={arianeOrganisation(organisationId, organisation?.nom, "campagnes")} />

      <PageHeader
        titre="Campagnes"
        sousTitre={
          vue
            ? `${nomOrganisation} · ${nbActives} campagne${nbActives > 1 ? "s" : ""}`
            : "Chargement…"
        }
        actions={
          organisationId ? (
            <Link to={`/organisations/${organisationId}/campagnes/creer`}>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle campagne
              </Button>
            </Link>
          ) : undefined
        }
      />

      <DataTable
        colonnes={colonnes}
        donnees={vue?.lignes}
        cleColonnes="campagnes-org"
        hrefLigne={(ligne) => `/campagnes/${ligne._id}`}
        barreFiltres={BARRE_FILTRES}
        filtres={filtresEffectifs}
        onFiltresChange={setFiltres}
        vide={{
          titre: "Aucune campagne",
          description: organisationId
            ? "Cette organisation n'a pas encore de campagne."
            : "Aucune campagne sans organisation.",
        }}
        videFiltre={{
          titre: "Aucune campagne pour ces filtres",
        }}
      />

      {vue?.tronque && (
        <p className="mt-2 text-sm text-muted-foreground">
          Seules les {vue.limite} campagnes les plus récentes sont affichées :
          les filtres et leurs compteurs ne portent que sur celles-ci.
        </p>
      )}

      {/* Pseudo-ligne « Hors campagne » : hors du tableau, parce qu'elle n'est
          pas une campagne — ni triable, ni filtrable avec les autres. Le
          portail client rend la même chose au même endroit. Sans compteur ici :
          le calculer voudrait dire lire des parcours dès l'ouverture de la
          page, ce que cette page s'interdit. */}
      <Card className="mt-4">
        <CardContent className="p-0">
          <Link
            to={horsCampagneHref}
            className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted/50"
          >
            <div>
              <div className="font-medium">Hors campagne</div>
              <div className="text-sm text-muted-foreground">
                Parcours créés sans campagne
              </div>
            </div>
            <Route className="h-4 w-4 text-muted-foreground" />
          </Link>
        </CardContent>
      </Card>
      <ConfirmerSuppression
        cible={aSupprimer ? { kind: "campagne", id: aSupprimer._id } : null}
        libelle={aSupprimer ? `« ${aSupprimer.nom} »` : ""}
        onClose={() => setASupprimer(null)}
      />
    </PageContainer>
  );
}
